import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  BatteryCharging,
  CheckCircle2,
  Clock3,
  Cloud,
  Container,
  Cpu,
  DatabaseBackup,
  Gauge,
  Globe2,
  HardDrive,
  HeartPulse,
  History,
  LockKeyhole,
  MemoryStick,
  RefreshCw,
  Server,
  ShieldCheck,
  Thermometer,
  TriangleAlert,
  WifiOff,
  XCircle,
} from "lucide-react";
import {
  calculateAvailability,
  CHECK_COPY,
  clamp,
  FRESHNESS_COPY,
  formatAbsoluteTime,
  formatBytes,
  formatDuration,
  formatPercent,
  formatRelativeTime,
  makeSparkline,
  STATUS_COPY,
} from "./lib/status";
import type {
  AwaitingResponse,
  CheckStatus,
  HistoryPoint,
  OperationalStatus,
  ProjectCheck,
  ProjectStatus,
  StatusResponse,
} from "./types";

const STATUS_ENDPOINT = "/api/v1/status";

function App() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [awaiting, setAwaiting] = useState<AwaitingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(Date.now());

  const fetchStatus = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const response = await fetch(STATUS_ENDPOINT, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const payload: unknown = await response.json();

      if (response.status === 503 && isAwaitingResponse(payload)) {
        setAwaiting(payload);
        setData(null);
        setError(null);
        return;
      }

      if (!response.ok || !isStatusResponse(payload)) {
        throw new Error("No fue posible leer una respuesta válida del monitor.");
      }

      setData(payload);
      setAwaiting(null);
      setError(null);
      setNow(Date.now());
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "No fue posible conectar con el monitor.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
    const refreshTimer = window.setInterval(() => void fetchStatus(), 60_000);
    const clockTimer = window.setInterval(() => setNow(Date.now()), 1_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void fetchStatus();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchStatus]);

  if (loading) return <LoadingScreen />;

  if (!data) {
    return (
      <EmptyScreen
        awaiting={awaiting}
        error={error}
        refreshing={refreshing}
        onRetry={() => void fetchStatus(true)}
      />
    );
  }

  const displayStatus: OperationalStatus =
    data.telemetry.state === "no_telemetry" ? "unknown" : data.overall.status;
  const availability = calculateAvailability(data.history);
  const runningProjects = data.projects.filter(
    (project) => project.status === "operational" || project.status === "degraded",
  ).length;
  const failedChecks = data.projects.flatMap((project) =>
    project.checks
      .filter((check) => check.status === "fail")
      .map((check) => ({ project, check })),
  );

  return (
    <div className="app-shell">
      <Header
        collectedAt={data.collectedAt}
        freshness={data.telemetry.state}
        now={now}
        refreshing={refreshing}
        onRefresh={() => void fetchStatus(true)}
      />

      <main id="contenido" className="page-content">
        <section className={`status-summary status-${displayStatus}`} aria-labelledby="estado-title">
          <div className="status-copy">
            <span className="status-icon" aria-hidden="true">
              {statusIcon(displayStatus)}
            </span>
            <div>
              <span className="eyebrow">Estado general</span>
              <h1 id="estado-title">{statusTitle(displayStatus, data.telemetry.state)}</h1>
              <p>{statusDescription(data)}</p>
              <div className="status-meta" aria-live="polite">
                <FreshnessBadge state={data.telemetry.state} />
                <time dateTime={data.collectedAt} title={formatAbsoluteTime(data.collectedAt)}>
                  Actualizado {formatRelativeTime(data.collectedAt, now)}
                </time>
              </div>
            </div>
          </div>

          <div className="status-glance" aria-label="Resumen rápido">
            <GlanceItem label="Servidor" value={STATUS_COPY[data.server.status].short} />
            <GlanceItem label="Proyectos disponibles" value={`${runningProjects} de ${data.projects.length}`} />
            <GlanceItem label="Tiempo activo" value={formatDuration(data.server.uptimeSeconds)} />
            <GlanceItem
              label="Tareas pendientes"
              value={data.overall.warningCount === 0 ? "Ninguna" : `${data.overall.warningCount}`}
              accent={data.overall.warningCount > 0}
            />
          </div>
        </section>

        {failedChecks.length > 0 && (
          <section className="critical-alert" role="alert" aria-labelledby="critical-alert-title">
            <span className="critical-alert-icon"><XCircle size={25} aria-hidden="true" /></span>
            <div>
              <strong id="critical-alert-title">
                {failedChecks.length === 1 ? "Una comprobación necesita atención" : `${failedChecks.length} comprobaciones necesitan atención`}
              </strong>
              {failedChecks.slice(0, 3).map(({ project, check }) => (
                <p key={`${project.id}-${check.id}`}><b>{project.name}</b>: {check.label}{check.detail ? ` · ${check.detail}` : ""}</p>
              ))}
            </div>
            <a href="#proyectos">Ver el proyecto</a>
          </section>
        )}

        {data.telemetry.state !== "fresh" && (
          <div className={`telemetry-notice ${data.telemetry.state}`} role="status">
            <WifiOff size={21} aria-hidden="true" />
            <div>
              <strong>{FRESHNESS_COPY[data.telemetry.state]}</strong>
              <span>
                {data.telemetry.state === "stale"
                  ? "Los valores pueden llevar retraso. Te mostramos la última medición confirmada."
                  : "Esto solo indica que no llegaron datos recientes; no confirma que el servidor esté caído."}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="inline-error" role="status">
            <TriangleAlert size={19} aria-hidden="true" />
            <span>No pudimos actualizar ahora. Los datos visibles corresponden a la última medición correcta.</span>
          </div>
        )}

        <section className="overview-grid" aria-label="Indicadores principales">
          <OverviewMetric
            icon={<ShieldCheck size={22} />}
            label="Disponibilidad observada"
            value={availability === null ? "Recopilando" : formatPercent(availability, 2)}
            detail={historyWindowLabel(data.history)}
          />
          <OverviewMetric
            icon={<Cpu size={22} />}
            label="Uso de CPU"
            value={formatPercent(data.server.cpu.usagePct)}
            detail={`${data.server.cpu.logicalCores} hilos · carga ${data.server.cpu.load1.toFixed(2)}`}
            level={data.server.cpu.usagePct}
          />
          <OverviewMetric
            icon={<MemoryStick size={22} />}
            label="Uso de memoria"
            value={formatPercent(data.server.memory.usagePct)}
            detail={`${formatBytes(data.server.memory.usedBytes)} de ${formatBytes(data.server.memory.totalBytes)}`}
            level={data.server.memory.usagePct}
          />
          <OverviewMetric
            icon={<HardDrive size={22} />}
            label="Uso del disco"
            value={formatPercent(data.server.disk.usagePct)}
            detail={`${formatBytes(data.server.disk.availableBytes ?? 0)} libres`}
            level={data.server.disk.usagePct}
          />
        </section>

        <section id="servidor" className="section-block" aria-labelledby="servidor-title">
          <SectionHeading
            eyebrow="Servidor"
            id="servidor-title"
            title="Salud del equipo principal"
            description="Consumo actual, tendencia y servicios que mantienen los proyectos en marcha."
            aside={<StatusPill status={data.server.status}>{STATUS_COPY[data.server.status].short}</StatusPill>}
          />

          <article className="server-card">
            <header className="server-header">
              <span className="server-avatar"><Server size={25} aria-hidden="true" /></span>
              <div>
                <small>Equipo supervisado</small>
                <h3>{data.server.displayName}</h3>
                <span>{data.server.id}</span>
              </div>
              <div className="uptime-block">
                <small>Encendido durante</small>
                <strong>{formatDuration(data.server.uptimeSeconds)}</strong>
              </div>
            </header>

            <div className="resource-grid">
              <ResourceMetric
                icon={<Cpu size={20} />}
                label="Procesador"
                value={data.server.cpu.usagePct}
                detail={`Carga en 1, 5 y 15 min: ${data.server.cpu.load1.toFixed(2)} · ${data.server.cpu.load5.toFixed(2)} · ${data.server.cpu.load15.toFixed(2)}`}
                history={data.history.map((point) => point.cpuPct)}
              />
              <ResourceMetric
                icon={<MemoryStick size={20} />}
                label="Memoria"
                value={data.server.memory.usagePct}
                detail={`${formatBytes(data.server.memory.usedBytes)} usados · ${formatBytes(data.server.memory.availableBytes ?? 0)} disponibles`}
                history={data.history.map((point) => point.memoryPct)}
              />
              <ResourceMetric
                icon={<HardDrive size={20} />}
                label="Disco raíz"
                value={data.server.disk.usagePct}
                detail={`${formatBytes(data.server.disk.usedBytes)} usados · ${formatBytes(data.server.disk.availableBytes ?? 0)} libres`}
                history={data.history.map((point) => point.diskPct)}
              />
            </div>

            <div className="server-details">
              <div className="services-card">
                <div className="card-heading">
                  <div>
                    <span className="card-icon"><Container size={19} aria-hidden="true" /></span>
                    <div><h3>Servicios del sistema</h3><p>Procesos esenciales supervisados</p></div>
                  </div>
                  {data.server.failedSystemUnits > 0 && (
                    <span className="attention-count">{data.server.failedSystemUnits} con fallo</span>
                  )}
                </div>
                <div className="service-list">
                  {data.server.services.map((service) => (
                    <div className="service-row" key={service.id}>
                      <span className={`service-dot ${service.status}`} aria-hidden="true" />
                      <div><strong>{service.label}</strong><span>{serviceStateCopy(service.status)}</span></div>
                      <CheckLabel status={service.status === "active" ? "pass" : service.status === "unknown" ? "unknown" : "fail"} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="host-facts-card">
                <div className="card-heading">
                  <div>
                    <span className="card-icon"><Gauge size={19} aria-hidden="true" /></span>
                    <div><h3>Otros indicadores</h3><p>Lecturas complementarias del equipo</p></div>
                  </div>
                </div>
                <div className="host-facts">
                  <HostFact
                    icon={<Container size={18} />}
                    label="Docker"
                    value={`${data.server.docker.running} activos`}
                    detail={`${serviceStateCopy(data.server.docker.status)} · ${data.server.docker.unhealthy} no saludables · ${data.server.docker.stopped} detenidos`}
                  />
                  <HostFact
                    icon={<Gauge size={18} />}
                    label="Swap"
                    value={formatPercent(data.server.swap.usagePct)}
                    detail={`${formatBytes(data.server.swap.usedBytes)} de ${formatBytes(data.server.swap.totalBytes)}`}
                  />
                  <HostFact
                    icon={<Thermometer size={18} />}
                    label="Temperatura"
                    value={data.server.temperatureC === null ? "No disponible" : `${data.server.temperatureC.toFixed(0)} °C`}
                    detail="Máximo informado por los sensores"
                  />
                  <HostFact
                    icon={<BatteryCharging size={18} />}
                    label="Batería"
                    value={
                      !data.server.battery.present || data.server.battery.percent === null
                        ? "No disponible"
                        : formatPercent(data.server.battery.percent)
                    }
                    detail={data.server.battery.state ?? "Estado no reportado"}
                  />
                </div>
              </div>
            </div>
          </article>
        </section>

        <section id="proyectos" className="section-block" aria-labelledby="proyectos-title">
          <SectionHeading
            eyebrow="Proyectos"
            id="proyectos-title"
            title="Servicios alojados"
            description="Qué está disponible, cuánto consume y qué tareas de protección siguen pendientes."
            aside={<span className="project-count">{data.projects.length} proyectos</span>}
          />

          <div className="project-grid">
            {data.projects.map((project) => (
              <ProjectCard key={project.id} project={project} now={now} />
            ))}
          </div>
        </section>

        <section id="actividad" className="section-block" aria-labelledby="actividad-title">
          <SectionHeading
            eyebrow="Actividad"
            id="actividad-title"
            title="Cambios recientes"
            description="Historial real de las transiciones detectadas por el monitor."
          />
          <article className="activity-card">
            {data.events.length === 0 ? (
              <div className="empty-events">
                <span className="empty-icon"><CheckCircle2 size={24} aria-hidden="true" /></span>
                <div>
                  <strong>No hay cambios de estado registrados</strong>
                  <p>La actividad aparecerá aquí cuando el monitor detecte una transición.</p>
                </div>
              </div>
            ) : (
              <ol className="event-list">
                {data.events.slice(0, 8).map((event) => (
                  <li key={event.id}>
                    <span className={`event-dot status-${event.to}`} aria-hidden="true" />
                    <div className="event-copy">
                      <div><strong>{event.targetName}</strong><span>{event.message}</span></div>
                      <time dateTime={event.at} title={formatAbsoluteTime(event.at)}>
                        {formatRelativeTime(event.at, now)}
                      </time>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </article>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-brand"><span className="brand-mark small">D</span><strong>DQVI Estado</strong></div>
        <p>Última medición: <time dateTime={data.collectedAt}>{formatAbsoluteTime(data.collectedAt)}</time></p>
        <span>Esquema v{data.schemaVersion}</span>
      </footer>
    </div>
  );
}

function Header({
  collectedAt,
  freshness,
  now,
  refreshing,
  onRefresh,
}: {
  collectedAt: string;
  freshness: StatusResponse["telemetry"]["state"];
  now: number;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="site-header">
      <a className="brand" href="#contenido" aria-label="DQVI Estado, inicio">
        <span className="brand-mark">D</span>
        <span><strong>DQVI</strong><small>Estado del servidor</small></span>
      </a>
      <nav aria-label="Navegación principal">
        <a href="#servidor">Servidor</a>
        <a href="#proyectos">Proyectos</a>
        <a href="#actividad">Actividad</a>
      </nav>
      <div className="header-actions">
        <span className={`header-update ${freshness}`} aria-live="polite">
          <i aria-hidden="true" />
          <span><small>Última lectura</small><time dateTime={collectedAt}>{formatRelativeTime(collectedAt, now)}</time></span>
        </span>
        <button className="refresh-button" onClick={onRefresh} disabled={refreshing} type="button">
          <RefreshCw size={17} className={refreshing ? "spin" : ""} aria-hidden="true" />
          <span>{refreshing ? "Actualizando" : "Actualizar"}</span>
        </button>
      </div>
    </header>
  );
}

function GlanceItem({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return <div className={accent ? "glance-item accent" : "glance-item"}><span>{label}</span><strong>{value}</strong></div>;
}

function SectionHeading({
  eyebrow,
  id,
  title,
  description,
  aside,
}: {
  eyebrow: string;
  id: string;
  title: string;
  description: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2 id={id}>{title}</h2>
        <p>{description}</p>
      </div>
      {aside}
    </div>
  );
}

function OverviewMetric({
  icon,
  label,
  value,
  detail,
  level,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  level?: number;
}) {
  return (
    <article className="overview-card">
      <span className="overview-icon" aria-hidden="true">{icon}</span>
      <div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
      {typeof level === "number" && (
        <div className="mini-progress" role="progressbar" aria-label={`${label}: ${formatPercent(level)}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamp(level))}>
          <span className={level >= 90 ? "critical" : level >= 80 ? "warning" : ""} style={{ width: `${clamp(level)}%` }} />
        </div>
      )}
    </article>
  );
}

function ResourceMetric({
  icon,
  label,
  value,
  detail,
  history,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  detail: string;
  history: number[];
}) {
  const safeValue = clamp(value);
  const points = makeSparkline(history.length ? history : [safeValue]);
  const pressure = safeValue >= 90 ? "critical" : safeValue >= 80 ? "warning" : "normal";
  return (
    <article className={`resource-card pressure-${pressure}`}>
      <div className="resource-topline">
        <span className="resource-icon" aria-hidden="true">{icon}</span>
        <div><span>{label}</span><strong>{formatPercent(safeValue)}</strong></div>
      </div>
      <div className="resource-visual">
        <div className="progress-track" role="progressbar" aria-label={`${label}: ${formatPercent(safeValue)}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(safeValue)}>
          <span style={{ width: `${safeValue}%` }} />
        </div>
        <svg className="sparkline" viewBox="0 0 240 72" role="img" aria-label={`Tendencia reciente de ${label}`}>
          <title>{`Tendencia reciente de ${label}`}</title>
          <line x1="0" y1="36" x2="240" y2="36" />
          <polyline points={points} fill="none" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <small>{detail}</small>
    </article>
  );
}

function HostFact({ icon, label, value, detail }: { icon: React.ReactNode; label: string; value: string; detail: string }) {
  return (
    <div className="host-fact">
      <span aria-hidden="true">{icon}</span>
      <div><small>{label}</small><strong>{value}</strong><p>{detail}</p></div>
    </div>
  );
}

function ProjectCard({ project, now }: { project: ProjectStatus; now: number }) {
  const health = projectHealthInfo(project);
  const memoryDetail = project.resources.memoryLimitBytes
    ? `${formatBytes(project.resources.memoryBytes)} / ${formatBytes(project.resources.memoryLimitBytes)}`
    : formatBytes(project.resources.memoryBytes);
  const attentionChecks = project.checks.filter((check) => check.status !== "pass");
  const passedChecks = project.checks.filter((check) => check.status === "pass");
  const hasCriticalCheck = attentionChecks.some((check) => check.status === "fail");
  const hasBackupTask = project.backup.status === "warn" || project.backup.status === "unknown";

  return (
    <article className={`project-card status-${project.status}`}>
      <header className="project-header">
        <span className="project-icon" aria-hidden="true">
          {project.access.visibility === "public" ? <Cloud size={23} /> : <LockKeyhole size={23} />}
        </span>
        <div><small>{project.id}</small><h3>{project.name}</h3></div>
        <StatusPill status={project.status}>{projectStatusCopy(project)}</StatusPill>
      </header>

      <p className="project-description">{project.description}</p>

      <div className={`health-summary check-${health.status}`}>
        <span>{checkIcon(health.status)}</span>
        <div><strong>{health.title}</strong><p>{health.description}</p></div>
      </div>

      <div className="project-access">
        <div>
          {project.access.visibility === "public" ? <Globe2 size={17} aria-hidden="true" /> : <LockKeyhole size={17} aria-hidden="true" />}
          <span><small>Acceso</small><strong>{project.access.visibility === "public" ? "Público" : "Privado"}</strong></span>
        </div>
        <div>
          <Activity size={17} aria-hidden="true" />
          <span><small>Respuesta</small><strong>{project.access.visibility === "private" ? "No aplica" : reachabilityCopy(project.access.reachable)}</strong></span>
        </div>
        <div>
          <Clock3 size={17} aria-hidden="true" />
          <span><small>Latencia</small><strong>{project.access.visibility === "private" ? "No aplica" : project.access.latencyMs === null ? "Sin medir" : `${Math.round(project.access.latencyMs)} ms`}</strong></span>
        </div>
        {project.access.url ? (
          <a href={project.access.url} target="_blank" rel="noreferrer">Abrir <ArrowUpRight size={15} aria-hidden="true" /></a>
        ) : (
          <span className="private-note">Sin puertos públicos</span>
        )}
      </div>

      <div className="project-stats" aria-label={`Recursos de ${project.name}`}>
        <ProjectStat label="CPU" value={formatPercent(project.resources.cpuPct, 1)} />
        <ProjectStat label="Memoria" value={memoryDetail} />
        <ProjectStat label="Servicios" value={`${project.runtime.runningServices}/${project.runtime.totalServices}`} />
        <ProjectStat label="Reinicios" value={`${project.runtime.restartCount}`} />
      </div>

      {(attentionChecks.length > 0 || hasBackupTask) && (
        <div className={`project-alerts ${hasCriticalCheck ? "critical" : "pending"}`}>
          <div className="project-alert-title">
            {hasCriticalCheck ? <XCircle size={20} aria-hidden="true" /> : <TriangleAlert size={20} aria-hidden="true" />}
            <strong>{hasCriticalCheck ? "Alerta activa" : "Tareas pendientes"}</strong>
          </div>
          {attentionChecks.map((check) => (
            <div className={`attention-row check-${check.status}`} key={check.id}>
              <span className="check-symbol">{checkIcon(check.status)}</span>
              <div><strong>{check.label}</strong><p>{check.detail ?? CHECK_COPY[check.status]}</p></div>
              <CheckLabel status={check.status} />
            </div>
          ))}
          {hasBackupTask && (
            <div className="attention-row check-warn">
              <span className="check-symbol"><DatabaseBackup size={17} aria-hidden="true" /></span>
              <div><strong>Protección externa por confirmar</strong><p>{project.backup.label}</p></div>
              <CheckLabel status={project.backup.status} />
            </div>
          )}
        </div>
      )}

      <div className={`backup-card check-${project.backup.status}`}>
        <span className="backup-icon"><DatabaseBackup size={19} aria-hidden="true" /></span>
        <div><small>Protección de datos</small><strong>{project.backup.label}</strong></div>
        <CheckLabel status={project.backup.status} />
      </div>

      <details className="project-details">
        <summary>Ver detalles operativos</summary>
        <dl>
          <DetailItem label="Estado del runtime" value={runtimeCopy(project.runtime.state)} />
          <DetailItem label="Tiempo activo" value={project.runtime.uptimeSeconds === null ? "No reportado" : formatDuration(project.runtime.uptimeSeconds)} />
          <DetailItem label="Servicios saludables" value={`${project.runtime.healthyServices}`} />
          <DetailItem label="Servicios no saludables" value={`${project.runtime.unhealthyServices}`} />
          <DetailItem label="Acceso verificado" value={project.access.checkedAt ? formatAbsoluteTime(project.access.checkedAt) : "No reportado"} />
          <DetailItem label="Último respaldo verificado" value={project.backup.lastVerifiedAt ? formatAbsoluteTime(project.backup.lastVerifiedAt) : "No reportado"} />
          <DetailItem label="Última restauración verificada" value={project.backup.restoreVerifiedAt ? formatAbsoluteTime(project.backup.restoreVerifiedAt) : "No reportado"} />
          <DetailItem label="Versión desplegada" value={project.deployment.version ?? "No reportada"} />
          <DetailItem label="Fecha de despliegue" value={project.deployment.deployedAt ? formatAbsoluteTime(project.deployment.deployedAt) : "No reportada"} />
          <DetailItem label="Controles correctos" value={`${passedChecks.length} de ${project.checks.length}`} />
        </dl>
        {passedChecks.length > 0 && (
          <div className="passed-checks">
            <h4>{passedChecks.length} {passedChecks.length === 1 ? "control correcto" : "controles correctos"}</h4>
            {passedChecks.map((check) => <ProjectCheckRow check={check} now={now} key={check.id} />)}
          </div>
        )}
      </details>
    </article>
  );
}

function ProjectStat({ label, value }: { label: string; value: string }) {
  return <div><small>{label}</small><strong>{value}</strong></div>;
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function ProjectCheckRow({ check, now }: { check: ProjectCheck; now: number }) {
  return (
    <div className={`check-row check-${check.status}`}>
      <span className="check-symbol">{checkIcon(check.status)}</span>
      <div>
        <strong>{check.label}</strong>
        <span>{check.detail ?? CHECK_COPY[check.status]}{typeof check.latencyMs === "number" && ` · ${Math.round(check.latencyMs)} ms`}</span>
      </div>
      {check.checkedAt && <time dateTime={check.checkedAt} title={formatAbsoluteTime(check.checkedAt)}>{formatRelativeTime(check.checkedAt, now)}</time>}
    </div>
  );
}

function StatusPill({ status, children }: { status: OperationalStatus; children: React.ReactNode }) {
  return <span className={`status-pill status-${status}`}><i aria-hidden="true" />{children}</span>;
}

function FreshnessBadge({ state }: { state: StatusResponse["telemetry"]["state"] }) {
  return <span className={`freshness-badge ${state}`}>{state === "fresh" ? <HeartPulse size={15} aria-hidden="true" /> : <WifiOff size={15} aria-hidden="true" />}{FRESHNESS_COPY[state]}</span>;
}

function CheckLabel({ status }: { status: CheckStatus }) {
  return <span className={`check-label check-${status}`}>{CHECK_COPY[status]}</span>;
}

function LoadingScreen() {
  return (
    <div className="loading-screen" aria-busy="true" aria-label="Cargando estado">
      <div className="loading-card">
        <div className="loading-brand"><span className="brand-mark">D</span><strong>DQVI Estado</strong></div>
        <span className="loading-icon"><Activity size={28} aria-hidden="true" /></span>
        <h1>Consultando el servidor</h1>
        <p>Estamos buscando la última medición confirmada.</p>
        <div className="loading-line" aria-hidden="true"><span /></div>
      </div>
    </div>
  );
}

function EmptyScreen({ awaiting, error, refreshing, onRetry }: { awaiting: AwaitingResponse | null; error: string | null; refreshing: boolean; onRetry: () => void }) {
  const isAwaiting = Boolean(awaiting);
  return (
    <div className="empty-screen">
      <header className="empty-header"><a className="brand" href="/" aria-label="DQVI Estado, inicio"><span className="brand-mark">D</span><span><strong>DQVI</strong><small>Estado del servidor</small></span></a></header>
      <main id="contenido">
        <span className="empty-state-icon">{isAwaiting ? <History size={32} aria-hidden="true" /> : <WifiOff size={32} aria-hidden="true" />}</span>
        <span className="eyebrow">Monitor de infraestructura</span>
        <h1>{isAwaiting ? "Sin lectura disponible" : "No pudimos consultar el estado"}</h1>
        <p>{isAwaiting ? "La fuente todavía no tiene un estado válido disponible. El panel volverá a consultarla automáticamente." : error ?? "La consulta no respondió. Puedes volver a intentarlo en unos segundos."}</p>
        <button className="primary-button" onClick={onRetry} disabled={refreshing} type="button"><RefreshCw size={17} className={refreshing ? "spin" : ""} aria-hidden="true" />{refreshing ? "Consultando…" : "Volver a intentar"}</button>
      </main>
    </div>
  );
}

function checkIcon(status: CheckStatus) {
  if (status === "pass") return <CheckCircle2 size={17} aria-hidden="true" />;
  if (status === "warn") return <TriangleAlert size={17} aria-hidden="true" />;
  if (status === "fail") return <XCircle size={17} aria-hidden="true" />;
  return <Activity size={17} aria-hidden="true" />;
}

function statusIcon(status: OperationalStatus) {
  if (status === "operational") return <CheckCircle2 size={38} />;
  if (status === "outage") return <XCircle size={38} />;
  if (status === "unknown") return <WifiOff size={38} />;
  return <TriangleAlert size={38} />;
}

function statusTitle(status: OperationalStatus, freshness: StatusResponse["telemetry"]["state"]): string {
  if (freshness === "no_telemetry") return "Esperando datos recientes";
  const copy: Record<OperationalStatus, string> = {
    operational: "Todo está funcionando",
    degraded: "Hay tareas que revisar",
    outage: "Hay una interrupción activa",
    maintenance: "Mantenimiento en curso",
    unknown: "Estado por confirmar",
  };
  return copy[status];
}

function statusDescription(data: StatusResponse): string {
  if (data.telemetry.state === "no_telemetry") return "Conservamos la última lectura mientras esperamos una nueva señal del servidor.";
  if (data.telemetry.state === "stale") return "La última señal llegó con retraso. Cada dato conserva su hora de medición.";
  return data.overall.summary;
}

function projectHealthInfo(project: ProjectStatus): { status: CheckStatus; title: string; description: string } {
  const hasFailure = project.checks.some((check) => check.status === "fail") || project.runtime.unhealthyServices > 0;
  const needsFunctionalCheck = project.checks.length === 0 || project.checks.some((check) => check.status === "unknown");
  if (project.runtime.state === "running" && needsFunctionalCheck && !hasFailure) {
    return {
      status: "unknown",
      title: "En ejecución · control de salud pendiente",
      description: "Docker confirma que el proceso está activo, pero aún no existe una prueba funcional.",
    };
  }
  if (hasFailure) return { status: "fail", title: "Hay una comprobación fallando", description: "El proceso necesita revisión; consulta los controles que aparecen abajo." };
  if (project.runtime.state === "running") return { status: "pass", title: "En ejecución · controles correctos", description: "El proceso y sus comprobaciones responden como se espera." };
  if (project.runtime.state === "partial") return { status: "warn", title: "Disponible parcialmente", description: "Solo una parte de los servicios está en ejecución." };
  if (project.runtime.state === "stopped") return { status: "fail", title: "Proceso detenido", description: "Los servicios del proyecto no están en ejecución." };
  return { status: "unknown", title: "Salud sin confirmar", description: "Aún no hay suficiente información para confirmar su funcionamiento." };
}

function projectStatusCopy(project: ProjectStatus): string {
  if (project.status === "degraded" && project.runtime.state === "running" && !project.checks.some((check) => check.status === "fail")) return "Disponible · revisar";
  const copy: Record<OperationalStatus, string> = { operational: "Disponible", degraded: "Requiere atención", outage: "Interrumpido", maintenance: "Mantenimiento", unknown: "Sin confirmar" };
  return copy[project.status];
}

function runtimeCopy(state: ProjectStatus["runtime"]["state"]): string {
  const copy: Record<ProjectStatus["runtime"]["state"], string> = { running: "En ejecución", partial: "Parcial", stopped: "Detenido", unknown: "Sin confirmar" };
  return copy[state];
}

function reachabilityCopy(reachable: boolean | null): string {
  if (reachable === null) return "Sin verificar";
  return reachable ? "Responde" : "Sin respuesta";
}

function serviceStateCopy(status: StatusResponse["server"]["docker"]["status"]): string {
  const copy = { active: "Activo", inactive: "Inactivo", failed: "Con fallo", unknown: "Sin confirmar" };
  return copy[status];
}

function historyWindowLabel(history: HistoryPoint[]): string {
  if (history.length < 2) return "Recopilando historial real";
  const first = Date.parse(history[0].at);
  const last = Date.parse(history[history.length - 1].at);
  return `Ventana observada: ${formatDuration(Math.max(0, (last - first) / 1000))}`;
}

function isStatusResponse(payload: unknown): payload is StatusResponse {
  if (!payload || typeof payload !== "object") return false;
  const value = payload as Partial<StatusResponse>;
  return value.schemaVersion === 1 && typeof value.collectedAt === "string" && Boolean(value.server) && Array.isArray(value.projects) && Array.isArray(value.history) && Array.isArray(value.events) && Boolean(value.telemetry);
}

function isAwaitingResponse(payload: unknown): payload is AwaitingResponse {
  if (!payload || typeof payload !== "object") return false;
  return (payload as Partial<AwaitingResponse>).state === "awaiting_data";
}

export default App;
